import { List, Orderable } from '@/components/form/modifiers/Orderable';
import { CategoryIcons } from '@/components/icons/CategoryIcons';
import { SidebarLink } from '@/components/sidebar/SidebarLink';
import { SidebarMainSection } from '@/components/sidebar/SidebarMainSection';
import { SidebarSubSection } from '@/components/sidebar/SidebarSubSection';
import { cn } from '@/lib/utils';
import { useCategories } from '@/services/api/hooks/catalog';
import { useUpdateDisplayPreferences } from '@/services/api/hooks/settings';
import { RoleType, useRole } from '@/services/authentication/hooks/user';
import { FC, ReactNode, useCallback } from 'react';
import { FiEdit } from 'react-icons/fi';
import SiEyeOff from '@/icons/SiEyeOff';

export interface AdditionalCatalogueLinks {
  placeAtStart?: boolean;
  element: ReactNode;
}

interface Props {
  baseUrl: string;
  baseId: string;
  onEdit?: (categoryId: string) => void;
  mainSection?: boolean;
  additionalLinks?: AdditionalCatalogueLinks[];
  catalogueTitleOverride?: string;
}

export const CategoryLinks: FC<Props> = ({
  baseUrl,
  baseId,
  onEdit,
  mainSection = false,
  additionalLinks = [],
  catalogueTitleOverride
}) => {
  const [categories] = useCategories();
  const role = useRole();

  const filteredCategories = categories.filter(c => {
    if (role.type === RoleType.Admin) return true;
    
    const isInternal = c.hiddenFromTenantIds?.includes("00000000-0000-0000-0000-000000000000");
    
    if (role.type === RoleType.Partner) {
      return !isInternal && !c.hiddenFromTenantIds?.includes(role.partnerId!);
    }
    if (role.type === RoleType.Company) {
      return !isInternal && !c.hiddenFromTenantIds?.includes(role.customerId!) && !c.hiddenFromTenantIds?.includes(role.partnerId!);
    }
    return true;
  });

  const updatePreferences = useUpdateDisplayPreferences();

  const onReorder = useCallback(
    async (source: number, destination: number) => {
      const array = [...filteredCategories];
      const [removed] = array.splice(source, 1);
      array.splice(destination, 0, removed);
      await updatePreferences.mutateAsync({
        orderedCategoryIds: array.map(item => item.id)
      });
    },
    [filteredCategories]
  );

  const content =
    role.type === RoleType.Admin ? (
      <List
        id="categories"
        itemIds={filteredCategories.map(item => item.id)}
        className={cn(filteredCategories.length > 0 && `mb-2 -mt-2`)}
        onReorder={onReorder}
      >
        {filteredCategories.map((c, index) => (
          <Orderable className="justify-start gap-1" key={`categoryOrderable-${index}`} id={c.id} index={index}>
            <div key={`${baseId}-${c.id}`} className="flex gap-2 justify-between group">
              <SidebarLink to={`${baseUrl}/${c.id}`}>
                <div className="flex gap-2 items-center">
                  <span className="text-2xl">{CategoryIcons[c.description]}</span>
                  <span>{c.name}</span>
                  {(c.hiddenFromTenantIds?.length ?? 0) > 0 && (
                    <span title="Hidden from some tenants" className="text-gray-400">
                      <SiEyeOff className="text-lg" />
                    </span>
                  )}
                </div>
              </SidebarLink>
              {onEdit && (
                <button
                  className="invisible group-hover:visible"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    onEdit(c.id);
                  }}
                >
                  <FiEdit />
                </button>
              )}
            </div>
          </Orderable>
        ))}
      </List>
    ) : (
      filteredCategories.map(c => (
        <div key={`${baseId}-${c.id}`} className="flex gap-2 justify-between group">
          <SidebarLink to={`${baseUrl}/${c.id}`}>
            <div className="flex gap-2">
              <span className="text-2xl">{CategoryIcons[c.description]}</span>
              <span>{c.name}</span>
            </div>
          </SidebarLink>
          {onEdit && (
            <button
              className="invisible group-hover:visible"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                onEdit(c.id);
              }}
            >
              <FiEdit />
            </button>
          )}
        </div>
      ))
    );

  const additionalStartLinks = additionalLinks.filter(link => link.placeAtStart);

  const catalogueTitle = catalogueTitleOverride ?? 'Catalogue';
  return mainSection ? (
    <SidebarMainSection title={catalogueTitle} sectionUrlIdentifier={baseId} to={baseUrl} collapsible>
      {additionalStartLinks.map(el => el.element)}
      {content}
    </SidebarMainSection>
  ) : (
    <SidebarSubSection title={catalogueTitle} to={baseUrl} collapsible defaultExpanded>
      {additionalStartLinks.map(el => el.element)}
      {content}
    </SidebarSubSection>
  );
};
